import { ComputedRef, computed } from '@vue/composition-api';
import boardEntryApi from '@/apis/board_entry';
import { BoardEntryModalState, BoardEntryConv } from '@/models/settouMessageBoard';
import { SearchParams } from '@/components/SettouMessageBoard/utils/index';
import { createInjection } from '@/lib/createInjection';

interface BoardEntryCrudOptions {
  modalState: BoardEntryModalState;
  getLatestSearchParams: () => SearchParams | null;
  searchBoardEntries: (params: SearchParams) => Promise<void>;
  reloadBoardEntries: () => Promise<void>;
}

export interface UseBoardEntryCrudResult {
  shouldShowEditModal: ComputedRef<boolean>;
  shouldShowDeleteModal: ComputedRef<boolean>;
  createBoardEntry: () => void;
  editBoardEntry: (origFormObj: BoardEntryConv) => void;
  addBoardEntry: () => Promise<void>;
  updateBoardEntry: () => Promise<void>;
  cancelEditBoardEntry: () => void;
  tryDeleteBoardEntry: (formObj: BoardEntryConv) => void;
  deleteBoardEntry: () => Promise<void>;
}

const { provide, inject } = createInjection<UseBoardEntryCrudResult>('settouMessageBoard/useBoardEntryCrud');

export function provideBoardEntryCrud(options: BoardEntryCrudOptions): void {
  provide(buildBoardEntryCrud(options));
}

export function useBoardEntryCrud(): UseBoardEntryCrudResult {
  return inject();
}

// 投稿のCRUD操作（編集・削除モーダルの制御を含む）を担う composable。
function buildBoardEntryCrud(options: BoardEntryCrudOptions): UseBoardEntryCrudResult {
  const {
    modalState,
    getLatestSearchParams,
    searchBoardEntries,
    reloadBoardEntries,
  } = options;

  const shouldShowEditModal = computed<boolean>(() => {
    if (modalState.shouldShowErrorModal) return false;
    return modalState.showEditModal;
  });
  const shouldShowDeleteModal = computed<boolean>(() => {
    if (modalState.shouldShowErrorModal) return false;
    return modalState.showDeleteModal;
  });

  const createBoardEntry = (): void => {
    modalState.showEditModal = true;
  };
  const editBoardEntry = (origFormObj: BoardEntryConv): void => {
    modalState.editingBoardEntry = origFormObj;
    modalState.showEditModal = true;
  };
  const addBoardEntry = async(): Promise<void> => {
    modalState.showEditModal = false;
    const latestSearchParams = getLatestSearchParams();
    if (!latestSearchParams) return;
    searchBoardEntries(latestSearchParams);
  };
  const updateBoardEntry = async(): Promise<void> => {
    modalState.showEditModal = false;
    modalState.editingBoardEntry = null;
    reloadBoardEntries();
  };
  const cancelEditBoardEntry = (): void => {
    modalState.editingBoardEntry = null;
    modalState.showEditModal = false;
  };
  const tryDeleteBoardEntry = (formObj: BoardEntryConv): void => {
    if (formObj.id) {
      modalState.destroyId = formObj.id;
    }
    modalState.deleteBody = `投稿No.${formObj.dispNo}を削除してもよろしいですか？`;
    modalState.showDeleteModal = true;
  };
  const deleteBoardEntry = async(): Promise<void> => {
    try {
      await boardEntryApi.destroy(modalState.destroyId || 0);
      reloadBoardEntries();
      modalState.showDeleteModal = false;
    } catch (e) {
      console.error('error', e);
      modalState.errorBody = '削除に失敗しました。再度操作を行ってください';
      modalState.shouldShowErrorModal = true;
    }
  };

  return {
    shouldShowEditModal,
    shouldShowDeleteModal,
    createBoardEntry,
    editBoardEntry,
    addBoardEntry,
    updateBoardEntry,
    cancelEditBoardEntry,
    tryDeleteBoardEntry,
    deleteBoardEntry,
  };
}
